import { SubscriptionTier } from '@workspace/database/schema';

import { getAppSumoTierLimits, isAppSumoTier } from './appsumo/tier-limits';

/**
 * Every tier whose limits come from the static TIER_LIMITS table. AppSumo
 * orgs are deliberately excluded: their numbers depend on `appsumoTier`, so
 * indexing TIER_LIMITS with a variable tier is a compile error and every such
 * site must go through resolveTierLimits().
 */
export type StripeTier = Exclude<SubscriptionTier, SubscriptionTier.APPSUMO>;

// Master kill switch for the BYOK (bring-your-own-key) ElevenLabs voice
// library. When true: show the "Voice Library" nav entry + page where
// customers connect their own ElevenLabs API key and sync their owned
// voices. BYOK generation is billed to the customer's own ElevenLabs plan,
// so there is intentionally NO per-tier custom-voice slot cap.
export const CUSTOM_VOICES_FEATURE_ENABLED = true;

/**
 * Every metered usage bucket, in one client-safe place. The server-only
 * `helpers/organization-limits` re-exports `UsageType` from here, the admin
 * adjust-usage schema validates against the array, and the dev +/- controls
 * type their prop with it — so the three can no longer drift.
 */
export const USAGE_TYPES = [
  'videoUpload',
  'videoAnalysis',
  'voiceOver',
  'renders',
  'storage',
  'translation',
  'avatarMinutes',
  'photoAvatarTrainings',
  'digitalTwinTrainings'
] as const;

export type UsageType = (typeof USAGE_TYPES)[number];

export type TierLimits = {
  maxVideoUploadMinutes: number;
  maxVideoLength: number;
  maxStorageBytes: number;
  maxVoiceOverMinutes: number;
  maxRenders: number;
  maxTranslations: number;
  resetType: 'one-time' | 'monthly';
  hasWatermark: boolean;
  hasVoiceoverGracePeriod: boolean;
  maxAvatarMinutesPerMonth: number; // Minutes of HeyGen avatar video per billing cycle
  maxCustomAvatars: number; // Max custom avatar records for the org
  customAvatarUploadEnabled: boolean; // Whether org can upload custom avatars
  maxSeats: number; // Members allowed in the org, owner included
};

export const TIER_LIMITS: Record<StripeTier, TierLimits> = {
  // FREE grants NOTHING. The product is tried via the Stripe-native 7-day Plus
  // trial (card on file, not charged), not via a free-tier allowance. An org
  // sits on FREE only before starting that trial, or after a subscription ends
  // — in both cases it must start the trial / subscribe to do anything.
  [SubscriptionTier.FREE]: {
    maxVideoUploadMinutes: 0,
    maxVideoLength: 0,
    maxStorageBytes: 0,
    maxVoiceOverMinutes: 0,
    maxRenders: 0,
    maxTranslations: 0,
    resetType: 'one-time',
    hasWatermark: true,
    hasVoiceoverGracePeriod: false,
    maxAvatarMinutesPerMonth: 0,
    maxCustomAvatars: 0,
    customAvatarUploadEnabled: false,
    maxSeats: 1
  },
  [SubscriptionTier.TRIAL]: {
    maxVideoUploadMinutes: 5, // Monthly
    maxVideoLength: 300, // 5 minutes in seconds
    maxStorageBytes: 600 * 1024 * 1024, // 600 MB storage
    maxVoiceOverMinutes: 5, // Monthly
    maxRenders: 3, // Monthly
    maxTranslations: 1, // Monthly
    resetType: 'monthly', // Trial resets monthly!
    hasWatermark: true,
    hasVoiceoverGracePeriod: true,
    maxAvatarMinutesPerMonth: 0,
    maxCustomAvatars: 0,
    customAvatarUploadEnabled: false,
    maxSeats: 1
  },
  [SubscriptionTier.PLUS]: {
    maxVideoUploadMinutes: 30, // Per month
    maxVideoLength: 900, // 15 minutes in seconds
    maxStorageBytes: 10 * 1024 * 1024 * 1024, // 10 GB
    maxVoiceOverMinutes: 40,
    maxRenders: 30,
    maxTranslations: 15, // Per month
    resetType: 'monthly',
    hasWatermark: false,
    hasVoiceoverGracePeriod: false,
    maxAvatarMinutesPerMonth: 3,
    maxCustomAvatars: 1,
    customAvatarUploadEnabled: true,
    maxSeats: 1
  },
  [SubscriptionTier.TEAM]: {
    maxVideoUploadMinutes: 60, // Per month
    maxVideoLength: 1080, // 18 minutes in seconds
    maxStorageBytes: 20 * 1024 * 1024 * 1024, // 20 GB
    maxVoiceOverMinutes: 80,
    maxRenders: 60,
    maxTranslations: 30, // Per month
    resetType: 'monthly',
    hasWatermark: false,
    hasVoiceoverGracePeriod: false,
    maxAvatarMinutesPerMonth: 8,
    maxCustomAvatars: 3,
    customAvatarUploadEnabled: true,
    maxSeats: 5
  },
  [SubscriptionTier.PRO]: {
    maxVideoUploadMinutes: 120, // Per month
    maxVideoLength: 1200, // 20 minutes in seconds
    maxStorageBytes: 50 * 1024 * 1024 * 1024, // 50 GB
    maxVoiceOverMinutes: 200,
    maxRenders: 120,
    maxTranslations: 60, // Per month
    resetType: 'monthly',
    hasWatermark: false,
    hasVoiceoverGracePeriod: false,
    maxAvatarMinutesPerMonth: 20,
    maxCustomAvatars: 10,
    customAvatarUploadEnabled: true,
    maxSeats: 15
  }
};

export function getLimitsByTier(tier: StripeTier): TierLimits {
  return TIER_LIMITS[tier];
}

/**
 * Tier limits for ANY org row, including AppSumo lifetime orgs whose maxima
 * come from the deal matrix. Prefer this over getLimitsByTier wherever the
 * tier is read from the database.
 */
export function resolveTierLimits(row: {
  tier: SubscriptionTier;
  appsumoTier?: number | null;
}): TierLimits {
  if (row.tier !== SubscriptionTier.APPSUMO) {
    return TIER_LIMITS[row.tier];
  }
  const deal = getAppSumoTierLimits(
    isAppSumoTier(row.appsumoTier) ? row.appsumoTier : 1
  );
  return {
    maxVideoUploadMinutes: deal.monthly.uploadMinutes,
    maxVideoLength: deal.maxVideoLengthSeconds,
    maxStorageBytes: deal.maxStorageBytes,
    // One-time buckets live on creditPackBalances; the org-row twins stay 0.
    maxVoiceOverMinutes: 0,
    maxRenders: deal.monthly.cloudRenders,
    maxTranslations: 0,
    resetType: 'monthly',
    hasWatermark: false,
    hasVoiceoverGracePeriod: false,
    maxAvatarMinutesPerMonth: 0,
    maxCustomAvatars: 0,
    customAvatarUploadEnabled: true,
    maxSeats: deal.seats
  };
}

export type CreditPackLimits = {
  maxVideoUploadMinutes: number;
  maxVoiceOverMinutes: number;
  maxRenders: number;
  maxTranslations: number;
  maxStorageBytes: number;
  maxAvatarMinutes: number;
  maxCustomAvatars: number;
};

// Credit-pack limits curated to feel "PRO-equivalent but lighter" — every pack
// gives a credit-pack buyer access to the same FEATURES PRO has, but with
// slightly lower quantities so the prepaid pack stays differentiated from a
// recurring subscription. Reference: PRO is 20 avatar min + 10 custom avatars
// per month; pack tiers stay at or under those numbers as one-time grants.
export const CREDIT_PACK_LIMITS: Record<string, CreditPackLimits> = {
  'credit-pack-starter': {
    maxVideoUploadMinutes: 30,
    maxVoiceOverMinutes: 50,
    maxRenders: 30,
    maxTranslations: 15,
    maxStorageBytes: 10 * 1024 * 1024 * 1024, // 10 GB
    maxAvatarMinutes: 5,
    maxCustomAvatars: 1
  },
  'credit-pack-standard': {
    maxVideoUploadMinutes: 100,
    maxVoiceOverMinutes: 180,
    maxRenders: 100,
    maxTranslations: 50,
    maxStorageBytes: 30 * 1024 * 1024 * 1024, // 30 GB
    maxAvatarMinutes: 15,
    maxCustomAvatars: 3
  },
  'credit-pack-power': {
    maxVideoUploadMinutes: 250,
    maxVoiceOverMinutes: 450,
    maxRenders: 250,
    maxTranslations: 125,
    maxStorageBytes: 75 * 1024 * 1024 * 1024, // 75 GB
    maxAvatarMinutes: 25,
    maxCustomAvatars: 7
  },
  // Testimonial reward - granted automatically for a Senja testimonial, never
  // sold. Deliberately absent from billingConfig.products, so no Stripe price
  // resolves to it and the purchase path in upsert-order.ts cannot reach it.
  // Upload and voiceover match what the campaign email promised. The other
  // fields are NOT cosmetic: the typical recipient is a FREE-tier org, and
  // TIER_LIMITS[FREE] is zero across the board, so renders and storage at 0
  // would hand someone 30 upload minutes they can neither store nor export.
  // Avatar credits stay at 0 - HeyGen minutes are real cash per clip and were
  // never part of the offer.
  'credit-pack-testimonial': {
    maxVideoUploadMinutes: 30,
    maxVoiceOverMinutes: 60,
    maxRenders: 15,
    maxTranslations: 5,
    maxStorageBytes: 10 * 1024 * 1024 * 1024, // 10 GB
    maxAvatarMinutes: 0,
    maxCustomAvatars: 0
  }
};

/** Pack id granted for a Senja testimonial. Not purchasable - see above. */
export const TESTIMONIAL_REWARD_PACK_ID = 'credit-pack-testimonial';

export type AddonLimits = {
  usageType:
    | 'videoUpload'
    | 'voiceOver'
    | 'renders'
    | 'translations'
    | 'storage'
    | 'avatarMinutes'
    | 'customAvatars';
  amount: number; // For storage, in bytes
};

export const ADDON_LIMITS: Record<string, AddonLimits> = {
  'addon-upload': { usageType: 'videoUpload', amount: 30 },
  'addon-voice': { usageType: 'voiceOver', amount: 30 },
  'addon-render': { usageType: 'renders', amount: 30 },
  'addon-translation': { usageType: 'translations', amount: 30 },
  'addon-storage': { usageType: 'storage', amount: 10 * 1024 * 1024 * 1024 }, // 10 GB
  'addon-avatar': { usageType: 'avatarMinutes', amount: 10 }, // 10 avatar minutes
  'addon-custom-avatar': { usageType: 'customAvatars', amount: 1 } // +1 custom avatar slot
};

export function getCreditPackLimits(
  packId: string,
  quantity: number
): CreditPackLimits {
  const base = CREDIT_PACK_LIMITS[packId];
  if (!base) throw new Error(`Unknown credit pack: ${packId}`);
  return {
    maxVideoUploadMinutes: base.maxVideoUploadMinutes * quantity,
    maxVoiceOverMinutes: base.maxVoiceOverMinutes * quantity,
    maxRenders: base.maxRenders * quantity,
    maxTranslations: base.maxTranslations * quantity,
    maxStorageBytes: base.maxStorageBytes * quantity,
    maxAvatarMinutes: base.maxAvatarMinutes * quantity,
    maxCustomAvatars: base.maxCustomAvatars * quantity
  };
}

export function getAddonLimits(addonId: string): AddonLimits {
  const addon = ADDON_LIMITS[addonId];
  if (!addon) throw new Error(`Unknown add-on: ${addonId}`);
  return addon;
}

export function formatStorageLimit(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${bytes / (1024 * 1024 * 1024)} GB`;
  }
  return `${bytes / (1024 * 1024)} MB`;
}

export function formatMinutesLimit(minutes: number): string {
  return `${minutes} min${minutes !== 1 ? 's' : ''}`;
}

export function formatVideoLength(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min${minutes !== 1 ? 's' : ''}`;
}

/**
 * Check if tier change is a downgrade
 * Uses direct tier comparison based on hierarchy: FREE < PLUS < TEAM < PRO
 */
export function isDowngrade(
  fromTier: SubscriptionTier,
  toTier: SubscriptionTier
): boolean {
  // AppSumo tiers are ordered by appsumoTier inside packages/billing/src/appsumo;
  // the Stripe sync never sees them, so treat them as neutral here.
  if (
    fromTier === SubscriptionTier.APPSUMO ||
    toTier === SubscriptionTier.APPSUMO
  ) {
    return false;
  }
  // Define upgrade paths - if moving from a tier to another is not in this map, it's a downgrade
  const upgradePaths: Record<StripeTier, StripeTier[]> = {
    [SubscriptionTier.FREE]: [
      SubscriptionTier.TRIAL,
      SubscriptionTier.PLUS,
      SubscriptionTier.TEAM,
      SubscriptionTier.PRO
    ],
    [SubscriptionTier.TRIAL]: [
      SubscriptionTier.PLUS,
      SubscriptionTier.TEAM,
      SubscriptionTier.PRO
    ],
    [SubscriptionTier.PLUS]: [SubscriptionTier.TEAM, SubscriptionTier.PRO],
    [SubscriptionTier.TEAM]: [SubscriptionTier.PRO],
    [SubscriptionTier.PRO]: []
  };

  // If same tier, it's not a downgrade
  if (fromTier === toTier) {
    return false;
  }

  // If the toTier is not in the upgrade paths from fromTier, it's a downgrade
  return !upgradePaths[fromTier]?.includes(toTier);
}
